import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  ButtonBase,
  Stack,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  IconButton,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SendIcon from '@mui/icons-material/Send';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { useLocation, useNavigate } from 'react-router-dom';
import { createOrder } from '../../../../services/Admin/OrdersApi';
import { getClientMenus, getMyDrinks } from '../../../../services/Clint/UserOrdersApi';
import { resolveImageUrl } from '../../../../services/indexMediaBaseUrl';
import ClintOrderMenuSection from './ClintOrderMenuSection';
import NuvvyCharacter from '../../../../components/NuvvyCharacter';
import logo from '../../../../Assets/logo.png';

const nuvvyChoiceMessages = [
  'تعالى نكتشف الـNUVVY اللي يناسب مزاجك النهارده',
  'نفتخر في NUVVY أننا أول كافيه في مصر يوفر للعميل تجربة إنشاء مشروب مخصص له.',
  'تعالى نكتشف الـNUVVY اللي يناسب مزاجك النهارده',
  'تحب الـNUVVY بتاعك يكون عباره عن اية؟',
];

const nuvvyCharacterStates = ['talking', 'happy', 'talking', 'wave'];

const ClintMakeOrder = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const hasHandledIncomingDrink = useRef(false);

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const [orderMode, setOrderMode] = useState(null);
  const [characterState, setCharacterState] = useState('wave');
  const [choiceMessageIndex, setChoiceMessageIndex] = useState(0);
  const choiceMessage = nuvvyChoiceMessages[choiceMessageIndex];

  // Customer Information
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [phone2, setPhone2] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [address2, setAddress2] = useState('');
  
  // Order Details
  const [orderType, setOrderType] = useState('delivery'); // delivery, takeaway, tables
  const [selectedTable, setSelectedTable] = useState('');
  
  // Items
  const [products, setProducts] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [myDrinks, setMyDrinks] = useState([]);
  const [cart, setCart] = useState([]);
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });
  const [addItemDialog, setAddItemDialog] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState('product'); // product or drink
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemNote, setItemNote] = useState('');
  const [menuTab, setMenuTab] = useState('products');

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;

      const parsedUser = JSON.parse(storedUser);

      setCustomerName(parsedUser?.name || '');
      setPhone(parsedUser?.phone || '');
      setAddress(parsedUser?.address || '');
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error);
    }
  }, []);

  // Load Data
  useEffect(() => {
    loadMenuData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderMode) return undefined;

    setChoiceMessageIndex(0);
    setCharacterState('talking');

    const interval = window.setInterval(() => {
      setChoiceMessageIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % nuvvyChoiceMessages.length;
        setCharacterState(nuvvyCharacterStates[nextIndex] || 'talking');
        return nextIndex;
      });
    }, 4300);

    return () => window.clearInterval(interval);
  }, [orderMode]);

  useEffect(() => {
    const approvedDrinkResponse = location.state?.approvedDrink;
    const selectedDessert = location.state?.selectedDessert;

    if ((!approvedDrinkResponse && !selectedDessert) || hasHandledIncomingDrink.current) {
      return;
    }

    const approvedDrink = approvedDrinkResponse?.drink;
    const nextCartItems = [];

    if (approvedDrink?.id) {
      nextCartItems.push({
        id: `drink_${approvedDrink.id}_${Date.now()}`,
        product_id: null,
        drink_id: approvedDrink.id,
        name: approvedDrink.drink_name || approvedDrink.name || 'مشروب مخصص',
        quantity: 1,
        price: toNumber(approvedDrink.price),
        note: approvedDrink.taste || '',
        status: 'pending',
      });
    }

    if (selectedDessert?.product_id || selectedDessert?.id || selectedDessert?.value) {
      const productId = selectedDessert.product_id || selectedDessert.id || selectedDessert.value;

      nextCartItems.push({
        id: `product_${productId}_${Date.now()}`,
        product_id: productId,
        drink_id: null,
        name: selectedDessert.name || selectedDessert.label || 'إضافة مع المشروب',
        quantity: 1,
        price: toNumber(selectedDessert.price),
        note: selectedDessert.category || '',
        status: 'pending',
      });
    }

    if (!nextCartItems.length) {
      return;
    }

    hasHandledIncomingDrink.current = true;

    setOrderMode('custom');
    setCart((prev) => [...prev, ...nextCartItems]);
    showNotification(
      selectedDessert
        ? 'تم استلام المشروب المعتمد والإضافة المختارة داخل السلة'
        : 'تم استلام المشروب المعتمد وإضافته إلى السلة',
      'success'
    );
  }, [location.state]);

  const loadMenuData = async () => {
    try {
      const [menusResponse, myDrinksResponse] = await Promise.all([
        getClientMenus(),
        getMyDrinks(),
      ]);

      setProducts(menusResponse?.data?.products || []);
      setDrinks(menusResponse?.data?.drinks || []);
      setMyDrinks(myDrinksResponse?.data?.order || []);
    } catch (error) {
      console.error('Error loading menu:', error);
      showNotification('حدث خطأ أثناء تحميل القائمة', 'error');
    }
  };

  // Add Item to Cart
  const handleAddItemClick = (type) => {
    setSelectedItemType(type);
    setSelectedItem(null);
    setItemQuantity(1);
    setItemNote('');
    setAddItemDialog(true);
  };

  const handleQuickAddProduct = (product) => {
    setSelectedItemType('product');
    setSelectedItem(product);
    setItemQuantity(1);
    setItemNote('');
    setAddItemDialog(true);
  };

  const handleQuickAddDrink = (drink) => {
    setSelectedItemType('drink');
    setSelectedItem(drink);
    setItemQuantity(1);
    setItemNote('');
    setAddItemDialog(true);
  };

  const getItemImage = (item) => {
    if (!item?.image) return '/placeholder.png';
    return resolveImageUrl(item.image);
  };

  const isVisibleItem = (item) => item?.status !== 0 && item?.status !== '0';

  const activeProducts = products.filter(isVisibleItem);
  const activeDrinks = drinks.filter(isVisibleItem);
  const activeMyDrinks = myDrinks.filter(isVisibleItem);

  const handleConfirmAddItem = () => {
    if (!selectedItem) {
      showNotification('الرجاء اختيار عنصر', 'warning');
      return;
    }

    const newItem = {
      id: `${selectedItemType}_${selectedItem.id}_${Date.now()}`,
      product_id: selectedItemType === 'product' ? selectedItem.id : null,
      drink_id: selectedItemType === 'drink' ? selectedItem.id : null,
      name: selectedItem.name || selectedItem.drink_name,
      quantity: itemQuantity,
      price: toNumber(selectedItem.price),
      note: itemNote,
      status: 'pending',
    };

    setCart([...cart, newItem]);
    setAddItemDialog(false);
    showNotification('تمت إضافة العنصر للسلة', 'success');
  };

  // Remove Item from Cart
  const handleRemoveItem = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  // Update Item Quantity
  const handleUpdateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    setCart(cart.map(item => 
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    ));
  };

  // Calculate Total
  const calculateTotal = () => {
    return cart.reduce(
      (sum, item) => sum + (toNumber(item.price) * toNumber(item.quantity)),
      0
    );
  };

  // Submit Order
  const handleSubmitOrder = async () => {
    // Validation

    if (!phone.trim()) {
      showNotification('الرجاء إدخال رقم الهاتف', 'error');
      return;
    }
    if (cart.length === 0) {
      showNotification('الرجاء إضافة عناصر للطلب', 'error');
      return;
    }
    if (orderType === 'tables' && !selectedTable) {
      showNotification('الرجاء اختيار رقم الطاولة', 'error');
      return;
    }

    setLoading(true);

    try {
      const orderPayload = {
        order_type: orderType,
        type: 'client',
        customer_id: null,
        name: customerName,
        phone: phone,
        phone2: phone2 || null,
        phone3: null,
        email: email || null,
        address: orderType === 'delivery' ? address : null,
        address2: orderType === 'delivery' ? address2 : null,
        address3: null,
        table_id: orderType === 'tables' ? parseInt(selectedTable) : null,
        captain_order_id: null,
        delivery_id: null,
        date: new Date().toISOString().slice(0, 19).replace('T', ' '),
        items: cart.map(item => ({
          product_id: item.product_id,
          drink_id: item.drink_id,
          quantity: toNumber(item.quantity),
          price: toNumber(item.price),
          note: item.note || null,
          status: item.status,
        })),
      };
        
      await createOrder(orderPayload);
      
      showNotification('تم إنشاء الطلب بنجاح!', 'success');
      
      // Reset Form
      resetForm();
      setOrderMode(null);
    } catch (error) {
      console.error('Error creating order:', error);
      showNotification(error.message || 'حدث خطأ أثناء إنشاء الطلب', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Reset Form
  const resetForm = () => {
    setCustomerName('');
    setPhone('');
    setPhone2('');
    setEmail('');
    setAddress('');
    setAddress2('');
    setOrderType('delivery');
    setSelectedTable('');
    setCart([]);
  };

  const handleBackToModeSelection = () => {
    resetForm();
    setOrderMode(null);
  };

  // Show Notification
  const showNotification = (message, type) => {
    setNotification({ open: true, message, type });
  };

const pageTitle = orderMode === 'custom'
    ? 'إنشاء مشروب مميز ومخصص'
    : 'إنشاء طلب من المنيو';

    useEffect(() => {
		document.title = 'Nuvvy-انشاء طلب';
	}, []);
  return (
    <Box
      dir="rtl"
      sx={{
        minHeight: '100dvh',
        background: `radial-gradient(circle at 10% 8%, ${alpha(theme.palette.primary.main, 0.08)}, transparent 35%), radial-gradient(circle at 90% 82%, ${alpha(theme.palette.secondary.main, 0.08)}, transparent 32%), linear-gradient(180deg, #f8fbfb 0%, #eef3f7 100%)`,
        py: { xs: 0.75, md: 2.5 },
        overflowX: 'hidden',
      }}
    >
    <Container maxWidth="xl" sx={{ py: { xs: 0.75, md: 1 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {orderMode && (
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 1.5, md: 2 },
          borderRadius: 3,
          textAlign: 'center',
          background: `linear-gradient(125deg, ${alpha('#ffffff', 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 55%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          boxShadow: `0 10px 24px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 900, mb: 0.5 }}>
          Nuvvy - تجربة لا تُنسى
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.9 }}>
          نفتخر في NUVVY اننا اول كافيه في مصر بيوفر للعميل تجربة انشاء مشروب مخصص له
        </Typography>
      </Paper>
      )}

     
      {!orderMode ? (
        <>
          <Box
            dir="rtl"
            sx={{
              minHeight: { xs: 'auto', md: 'calc(100vh - 72px)' },
              display: 'grid',
              placeItems: 'center',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: { xs: 2.5, md: 4 },
              px: { xs: 1, sm: 2.5, md: 5 },
              py: { xs: 2, md: 5 },
              background:
                'linear-gradient(135deg, #f8fbf4 0%, #f1f7f3 48%, #fff8ec 100%)',
              border: '1px solid rgba(47, 87, 65, 0.14)',
              boxShadow: '0 24px 70px rgba(23, 53, 37, 0.10)',
              '@keyframes choiceFadeUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(14px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
              '@keyframes logoBreathe': {
                '0%, 100%': {
                  transform: 'translateY(0) scale(1)',
                  boxShadow: '0 16px 36px rgba(23, 53, 37, 0.16)',
                },
                '50%': {
                  transform: 'translateY(-4px) scale(1.03)',
                  boxShadow: '0 22px 46px rgba(23, 53, 37, 0.24)',
                },
              },
              '@keyframes featuredChoiceGlow': {
                '0%, 100%': {
                  boxShadow: '0 18px 38px rgba(156, 39, 176, 0.18)',
                },
                '50%': {
                  boxShadow: '0 24px 52px rgba(156, 39, 176, 0.30)',
                },
              },
              '@keyframes featuredIconPulse': {
                '0%, 100%': {
                  transform: 'scale(1) rotate(0deg)',
                },
                '50%': {
                  transform: 'scale(1.08) rotate(-7deg)',
                },
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(circle at 14% 18%, rgba(234, 247, 181, 0.55), transparent 34%), radial-gradient(circle at 88% 82%, rgba(47, 87, 65, 0.12), transparent 34%)',
                pointerEvents: 'none',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                maxWidth: 1120,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' },
                alignItems: 'center',
                gap: { xs: 2, md: 5 },
              }}
            >
              <Stack
                spacing={1.5}
                sx={{
                  order: { xs: 2, md: 1 },
                  textAlign: { xs: 'center', md: 'right' },
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.25,
                    alignSelf: { xs: 'center', md: 'flex-start' },
                    p: { xs: 0.8, sm: 1 },
                    pr: 1.4,
                    borderRadius: 999,
                    border: '1px solid rgba(47, 87, 65, 0.16)',
                    background: 'rgba(255, 255, 255, 0.78)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 16px 36px rgba(23, 53, 37, 0.13)',
                    animation: 'choiceFadeUp 520ms ease 620ms both',
                    transition: 'transform 180ms ease, box-shadow 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 20px 42px rgba(23, 53, 37, 0.18)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Nuvvy"
                    onMouseEnter={() => setCharacterState('happy')}
                    onMouseLeave={() => setCharacterState(nuvvyCharacterStates[choiceMessageIndex] || 'talking')}
                    sx={{
                      width: { xs: 42, sm: 52 },
                      height: { xs: 42, sm: 52 },
                      objectFit: 'contain',
                      borderRadius: '50%',
                      p: 0.45,
                      background: 'linear-gradient(145deg, #f8ffd8, #ffffff)',
                      border: '1px solid rgba(47, 87, 65, 0.14)',
                      animation: 'logoBreathe 3.8s ease-in-out infinite',
                      cursor: 'pointer',
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950, color: '#173525', lineHeight: 1 }}>
                      NUVVY
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                      اصنع مزاجك بنفسك
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="overline"
                  sx={{
                    color: 'success.dark',
                    fontWeight: 900,
                    letterSpacing: 0,
                    animation: 'choiceFadeUp 520ms ease 850ms both',
                  }}
                >
                  Nuvvy Experience
                </Typography>

                <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 0.5 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      maxWidth: 620,
                      fontWeight: 950,
                      lineHeight: 1.25,
                      color: '#173525',
                      animation: 'choiceFadeUp 560ms ease 920ms both',
                    }}
                  >
                    تعالى نكتشف الـNUVVY اللي يناسب مزاجك النهارده
                  </Typography>
                </Box>

              <Stack spacing={{ xs: 1, sm: 1.25 }} sx={{ width: '100%', maxWidth: 660, mx: { xs: 'auto', md: 0 } }}>
                  <ButtonBase
                    onClick={() => navigate('/clint/make-drink')}
                    onMouseEnter={() => setCharacterState('pointLeft')}
                    onMouseLeave={() => setCharacterState(nuvvyCharacterStates[choiceMessageIndex] || 'talking')}
                    sx={{
                      width: '100%',
                      textAlign: 'right',
                      borderRadius: 2.5,
                      overflow: 'visible',
                      animation: 'choiceFadeUp 520ms ease 1000ms both',
                    }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        p: { xs: 1.35, sm: 2.25 },
                        pt: { xs: 2.45, sm: 2.6 },
                        borderRadius: 2.5,
                        border: '2px solid rgba(156, 39, 176, 0.42)',
                        outline: '1px solid rgba(255, 255, 255, 0.85)',
                        outlineOffset: '-5px',
                        background:
                          'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,246,231,0.98) 42%, rgba(252,239,255,0.98) 100%)',
                        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                        cursor: 'pointer',
                        animation: 'featuredChoiceGlow 2.8s ease-in-out infinite',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          background:
                            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.62) 45%, transparent 70%)',
                          transform: 'translateX(120%) skewX(-18deg)',
                          transition: 'transform 650ms ease',
                          pointerEvents: 'none',
                        },
                        '&:hover': {
                          transform: { xs: 'none', md: 'translateX(-7px) scale(1.015)' },
                          borderColor: '#9c27b0',
                          boxShadow: '0 28px 58px rgba(156, 39, 176, 0.32)',
                          '&::before': {
                            transform: 'translateX(-120%) skewX(-18deg)',
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          left: 12,
                          px: 1.25,
                          py: 0.35,
                          borderRadius: 999,
                          color: '#5f176b',
                          fontWeight: 950,
                          fontSize: { xs: '0.72rem', sm: '0.78rem' },
                          background: 'linear-gradient(135deg, #fff6d7, #f0d4ff)',
                          border: '1px solid rgba(156, 39, 176, 0.22)',
                          boxShadow: '0 8px 18px rgba(156, 39, 176, 0.14)',
                          zIndex: 1,
                        }}
                      >
                        جرّب الجديد
                      </Box>
                      <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
                        <Box
                          sx={{
                            width: { xs: 48, sm: 58 },
                            height: { xs: 48, sm: 58 },
                            borderRadius: 2,
                            background: 'linear-gradient(145deg, #9c27b0, #ffb74d)',
                            color: '#fff',
                            display: 'grid',
                            placeItems: 'center',
                            flexShrink: 0,
                            boxShadow: '0 14px 26px rgba(156, 39, 176, 0.25)',
                            '& svg': {
                              animation: 'featuredIconPulse 1.9s ease-in-out infinite',
                            },
                          }}
                        >
                          <AutoFixHighIcon />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 950, color: '#22152b', fontSize: { xs: '0.98rem', sm: '1rem' } }}>
                            اصنع الـ NUVVY الخاص بك
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: { xs: '0.82rem', sm: '0.875rem' } }}>
                            كوّن مشروب جديد على مزاجك واختار مكوناته بنفسك.
                          </Typography>
                        </Box>
                        <KeyboardArrowLeftIcon sx={{ color: 'secondary.main', flexShrink: 0 }} />
                      </Stack>
                    </Paper>
                  </ButtonBase>

                  <ButtonBase
                    onClick={() => setOrderMode('menu')}
                    onMouseEnter={() => setCharacterState('pointRight')}
                    onMouseLeave={() => setCharacterState(nuvvyCharacterStates[choiceMessageIndex] || 'talking')}
                    sx={{
                      width: '100%',
                      textAlign: 'right',
                      borderRadius: 2,
                      overflow: 'hidden',
                      animation: 'choiceFadeUp 520ms ease 1150ms both',
                    }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        width: '100%',
                        p: { xs: 1.6, sm: 2 },
                        borderRadius: 2,
                        borderColor: 'rgba(47, 87, 65, 0.22)',
                        background:
                          'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(244,251,246,0.98) 100%)',
                        transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: { xs: 'none', md: 'translateX(-5px)' },
                          borderColor: 'success.main',
                          boxShadow: '0 18px 38px rgba(47, 87, 65, 0.14)',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
                        <Box
                          sx={{
                          width: { xs: 42, sm: 46 },
                          height: { xs: 42, sm: 46 },
                            borderRadius: 1.5,
                            bgcolor: 'success.light',
                            color: 'success.dark',
                            display: 'grid',
                            placeItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <RestaurantMenuIcon />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 950, color: '#173525', fontSize: { xs: '0.98rem', sm: '1rem' } }}>
                            استكشف المنيو
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, fontSize: { xs: '0.82rem', sm: '0.875rem' } }}>
                            اختار مشروب جاهز من قائمة Nuvvy.
                          </Typography>
                        </Box>
                        <KeyboardArrowLeftIcon sx={{ color: 'success.main', flexShrink: 0 }} />
                      </Stack>
                    </Paper>
                  </ButtonBase>
                </Stack>
              </Stack>

              <Box
                sx={{
                  order: { xs: 1, md: 2 },
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <NuvvyCharacter
                  state={characterState}
                  size="large"
                  showBubble
                  message={choiceMessage}
                />
              </Box>
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              display: 'none',
              p: { xs: 2, md: 3 },
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.09)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <RestaurantMenuIcon sx={{ color: '#fff', fontSize: 30 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 900, lineHeight: 1.2, mb: 0.5 }}>
                  إنشاء طلب جديد
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  اختر نوع الطلب المناسب للعميل — مشروب مميز أو اختيار من المنيو
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Chip label="مشروب مميز" size="small" color="secondary" variant="outlined" />
                  <Chip label="من المنيو" size="small" color="primary" variant="outlined" />
                  <Chip label="إضافة للسلة" size="small" variant="outlined" />
                </Stack>
              </Box>
            </Stack>
          </Paper>
          <Paper
            elevation={0}
            sx={{
              display: 'none',
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              textAlign: 'center',
              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              boxShadow: `0 14px 32px ${alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 900, mb: 1 }}>
              كيف تريد بدء الطلب؟
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              اختر ما إذا كان العميل يريد مشروبًا مميزًا ومخصصًا له أو يريد الاختيار مباشرة من المنيو.
            </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card
                onClick={() => navigate('/clint/make-drink')}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                  borderRadius: 3,
                  boxShadow: `0 8px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    transform: isMobile ? 'none' : 'translateY(-6px)',
                    boxShadow: isMobile ? undefined : 6,
                    borderColor: 'secondary.main',
                  },
                }}
              >
                <CardContent sx={{ py: 5 }}>
                  <AutoFixHighIcon sx={{ fontSize: 72, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    إنشاء مشروب مميز ومخصص
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    مناسب عندما يريد العميل مشروبًا خاصًا أو من قائمة المشروبات المميزة.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                onClick={() => setOrderMode('menu')}
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 3,
                  boxShadow: `0 8px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    transform: isMobile ? 'none' : 'translateY(-6px)',
                    boxShadow: isMobile ? undefined : 6,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <CardContent sx={{ py: 5 }}>
                  <RestaurantMenuIcon sx={{ fontSize: 72, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    الاختيار من المنيو
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    مناسب عندما يريد العميل اختيار صنف أو مشروب مباشر من القائمة المتاحة.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
        </>
      ) : (
        <>
          <Paper
            elevation={0}
          sx={{
              p: { xs: 1.25, sm: 2, md: 3 },
              mb: { xs: 1.25, md: 3 },
              borderRadius: { xs: 2.5, md: 3 },
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.09)} 0%, ${alpha(theme.palette.secondary.main, 0.06)} 100%)`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={{ xs: 1.25, sm: 2 }}>
                <Box
                  sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: 2.5,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {orderMode === 'custom' ? (
                    <AutoFixHighIcon sx={{ color: '#fff', fontSize: 28 }} />
                  ) : (
                    <RestaurantMenuIcon sx={{ color: '#fff', fontSize: 28 }} />
                  )}
                </Box>
                <Box>
                  <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 900, lineHeight: 1.2, mb: 0.75 }}>
                    {pageTitle}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    <Chip
                      label={`${cart.length} عنصر في السلة`}
                      size="small"
                      color="primary"
                      variant="filled"
                      icon={<ShoppingCartIcon />}
                    />
                    <Chip
                      label={`${calculateTotal().toFixed(2)} ج.م`}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Stack>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToModeSelection}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
                sx={{ borderRadius: 2, fontWeight: 700, alignSelf: { xs: 'stretch', sm: 'auto' } }}
              >
                رجوع
              </Button>
            </Stack>
          </Paper>

      <Grid container spacing={{ xs: 1.5, md: 3 }}>
        {/* Customer Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: { xs: 2.5, md: 3 }, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                معلومات العميل
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="اسم العميل *"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف *"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="رقم هاتف إضافي"
                    value={phone2}
                    onChange={(e) => setPhone2(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="البريد الإلكتروني"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Details */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', borderRadius: { xs: 2.5, md: 3 }, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                تفاصيل الطلب
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>نوع الطلب</InputLabel>
                    <Select
                      value={orderType}
                      label="نوع الطلب"
                      onChange={(e) => setOrderType(e.target.value)}
                    >
                      <MenuItem value="delivery">توصيل</MenuItem>
                      <MenuItem value="takeaway">تيك أواي</MenuItem>

                    </Select>
                  </FormControl>
                </Grid>

                {orderType === 'delivery' && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="العنوان"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        multiline
                        rows={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="تفاصيل إضافية للعنوان"
                        value={address2}
                        onChange={(e) => setAddress2(e.target.value)}
                      />
                    </Grid>
                  </>
                )}

                
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <ClintOrderMenuSection
          menuTab={menuTab}
          setMenuTab={setMenuTab}
          activeProducts={activeProducts}
          activeDrinks={activeDrinks}
          activeMyDrinks={activeMyDrinks}
          onQuickAddProduct={handleQuickAddProduct}
          onQuickAddDrink={handleQuickAddDrink}
          getItemImage={getItemImage}
          toNumber={toNumber}
        />

        {/* Cart (Right) */}
        <Grid item xs={12} md={8} sx={{ order: { xs: 1, md: 3 } }}>
          <Card sx={{ borderRadius: { xs: 2.5, md: 3 }, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  السلة ({cart.length})
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddItemClick('drink')}
                    color="secondary"
                    sx={{ borderRadius: 2, fontWeight: 700 }}
                  >
                    {orderMode === 'custom' ? 'إضافة مشروب مخصص' : 'إضافة مشروب'}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {cart.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    السلة فارغة. قم بإضافة عناصر للطلب.
                  </Typography>
                </Box>
              ) : (
                <>
                  {isMobile ? (
                    <Stack spacing={1}>
                      {cart.map((item) => (
                        <Paper
                          key={item.id}
                          variant="outlined"
                          sx={{ p: 1.25, borderRadius: 2.5, borderColor: alpha(theme.palette.divider, 0.9) }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                              {item.name}
                            </Typography>
                            <Chip
                              label={item.product_id ? 'منتج' : 'مشروب مخصص'}
                              color={item.product_id ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </Stack>

                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                            السعر: {toNumber(item.price).toFixed(2)} ج.م | الإجمالي: {(toNumber(item.price) * toNumber(item.quantity)).toFixed(2)} ج.م
                          </Typography>

                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <IconButton size="small" onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}>
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography sx={{ mx: 1.5, minWidth: 20, textAlign: 'center', fontWeight: 700 }}>{item.quantity}</Typography>
                              <IconButton size="small" onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}>
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <IconButton color="error" onClick={() => handleRemoveItem(item.id)}>
                              <DeleteIcon />
                            </IconButton>
                          </Stack>

                          {item.note ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              ملاحظة: {item.note}
                            </Typography>
                          ) : null}
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>الصنف</TableCell>
                            <TableCell>النوع</TableCell>
                            <TableCell align="center">الكمية</TableCell>
                            <TableCell align="right">السعر</TableCell>
                            <TableCell align="right">الإجمالي</TableCell>
                            <TableCell>ملاحظات</TableCell>
                            <TableCell align="center">إجراءات</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cart.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={item.product_id ? 'منتج' : 'مشروب مخصص'}
                                  color={item.product_id ? 'primary' : 'secondary'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography sx={{ mx: 2 }}>{item.quantity}</Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell align="right">{toNumber(item.price).toFixed(2)} ج.م</TableCell>
                              <TableCell align="right">
                                {(toNumber(item.price) * toNumber(item.quantity)).toFixed(2)} ج.م
                              </TableCell>
                              <TableCell>{item.note || '-'}</TableCell>
                              <TableCell align="center">
                                <IconButton
                                  color="error"
                                  onClick={() => handleRemoveItem(item.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      الإجمالي: {calculateTotal().toFixed(2)} ج.م
                    </Typography>
                    <Button
                      variant="contained"
                      size={isMobile ? 'medium' : 'large'}
                      startIcon={<SendIcon />}
                      onClick={handleSubmitOrder}
                      disabled={loading || cart.length === 0}
                      fullWidth={isMobile}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    >
                      {loading ? 'جاري الإرسال...' : 'إرسال الطلب'}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
        </>
      )}

      {/* Add Item Dialog */}
      <Dialog 
        open={addItemDialog} 
        onClose={() => setAddItemDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 } } }}
      >
        <DialogTitle>
          إضافة {selectedItemType === 'product' ? 'منتج' : 'مشروب مخصص'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {selectedItemType === 'drink' ? (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>اختر الصنف</InputLabel>
                  <Select
                    value={selectedItem?.id || ''}
                    label="اختر الصنف"
                    onChange={(e) => {
                      const item = activeDrinks.find(i => i.id === e.target.value);
                      setSelectedItem(item);
                    }}
                  >
                    {activeDrinks.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name || item.drink_name} - {item.price || 0} ج.م
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="الصنف المختار"
                  value={selectedItem?.name || '-'}
                  disabled
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="الكمية"
                value={itemQuantity}
                onChange={(e) => setItemQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ملاحظات (اختياري)"
                value={itemNote}
                onChange={(e) => setItemNote(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 1.5 }, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button fullWidth={isMobile} onClick={() => setAddItemDialog(false)}>إلغاء</Button>
          <Button fullWidth={isMobile} variant="contained" onClick={handleConfirmAddItem}>
            إضافة
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.type}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
    </Box>
  );
};

export default ClintMakeOrder;
